import { useState } from 'react';
import { Eye, EyeOff, KeyRound } from 'lucide-react';
import Modal from '../../components/common/Modal';

// Unauthenticated raw fetch, same pattern as AuthContext's login() — the
// shared api.js helpers assume a logged-in session (Authorization header),
// which doesn't apply here since the whole point is the user has no token.
async function callApi(path, body) {
  const res = await fetch(`/api/auth/forgot-password/${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message || 'Something went wrong');
  return data;
}

export default function ForgotPasswordModal({ isOpen, onClose }) {
  const [step, setStep]         = useState('phone'); // 'phone' | 'otp' | 'done'
  const [phone, setPhone]       = useState('');
  const [otp, setOtp]           = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [sentMessage, setSentMessage] = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);

  const reset = () => {
    setStep('phone'); setPhone(''); setOtp(''); setNewPassword(''); setConfirmPassword('');
    setSentMessage(''); setError(''); setLoading(false);
  };
  const handleClose = () => { reset(); onClose(); };

  const requestOtp = async () => {
    setError('');
    if (!phone.trim()) return setError('Enter your phone number.');
    setLoading(true);
    try {
      const data = await callApi('request', { phone: phone.trim() });
      setSentMessage(data.message || 'OTP sent via WhatsApp.');
      setStep('otp');
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  const resetPassword = async () => {
    setError('');
    if (!otp.trim()) return setError('Enter the OTP sent to your WhatsApp.');
    if (newPassword.length < 6) return setError('New password must be at least 6 characters.');
    if (newPassword !== confirmPassword) return setError('Passwords do not match.');
    setLoading(true);
    try {
      await callApi('reset', { phone: phone.trim(), otp: otp.trim(), newPassword });
      setStep('done');
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="🔐 Forgot Password">
      {step === 'phone' && (
        <div className="space-y-4">
          <p className="text-sm text-gray-500">
            Enter the phone number registered on your account (father, mother, teacher, or admin).
            We'll send a one-time code via WhatsApp.
          </p>
          <div>
            <label className="block text-sm font-medium mb-1">Phone Number</label>
            <input type="tel" value={phone} onChange={e => setPhone(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && requestOtp()}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
              placeholder="e.g. 9876543210" autoFocus />
          </div>
          {error && <div className="bg-red-50 text-red-600 text-sm px-3 py-2 rounded-lg border border-red-100">{error}</div>}
          <button onClick={requestOtp} disabled={loading}
            className="w-full flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2.5 rounded-lg disabled:opacity-60">
            <KeyRound size={16} />{loading ? 'Sending…' : 'Send OTP via WhatsApp'}
          </button>
        </div>
      )}

      {step === 'otp' && (
        <div className="space-y-4">
          <div className="bg-green-50 text-green-700 text-sm px-3 py-2 rounded-lg border border-green-100">{sentMessage}</div>
          <div>
            <label className="block text-sm font-medium mb-1">OTP</label>
            <input value={otp} onChange={e => setOtp(e.target.value)} maxLength={6}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 tracking-widest"
              placeholder="6-digit code" autoFocus />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">New Password</label>
            <div className="relative">
              <input type={showPass ? 'text' : 'password'} value={newPassword} onChange={e => setNewPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 pr-10"
                placeholder="Min. 6 characters" />
              <button type="button" onClick={() => setShowPass(s => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Confirm New Password</label>
            <input type={showPass ? 'text' : 'password'} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && resetPassword()}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
              placeholder="Re-enter new password" />
          </div>
          {error && <div className="bg-red-50 text-red-600 text-sm px-3 py-2 rounded-lg border border-red-100">{error}</div>}
          <div className="flex items-center justify-between">
            <button onClick={() => { setStep('phone'); setError(''); }} className="text-sm text-indigo-600 hover:underline">
              Change phone number
            </button>
            <button onClick={requestOtp} disabled={loading} className="text-sm text-indigo-600 hover:underline disabled:opacity-60">
              Resend OTP
            </button>
          </div>
          <button onClick={resetPassword} disabled={loading}
            className="w-full flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2.5 rounded-lg disabled:opacity-60">
            <KeyRound size={16} />{loading ? 'Resetting…' : 'Reset Password'}
          </button>
        </div>
      )}

      {step === 'done' && (
        <div className="space-y-4 text-center py-4">
          <div className="text-5xl">✅</div>
          <p className="font-semibold text-gray-800">Password reset successfully!</p>
          <p className="text-sm text-gray-500">You can now sign in with your new password.</p>
          <button onClick={handleClose}
            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2.5 rounded-lg">
            Back to Sign In
          </button>
        </div>
      )}
    </Modal>
  );
}
